import { BAND_WIDTH, formatReg, layoutPlate, PLATE_HEIGHT, renderPlateSvg } from '@aroo/shared';
import { useId, useMemo, type CSSProperties, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { isPlateValue, PLATE_SIZES, type PlateSize } from './plateValue';
import classes from './UkPlate.module.css';

export interface UkPlateProps {
  /** Any spelling: "ab12cde", "AB12 CDE". Placeholders (GUV, TBC, blank) render as text. */
  reg: string | null | undefined;
  size?: PlateSize;
  /** Blue UK identifier band; defaults on for md/lg, off for xs/sm (illegible there). */
  band?: 'uk' | 'none';
  /** rear = yellow (default), front = white. */
  variant?: 'rear' | 'front';
  /** Makes the plate a link (e.g. to the vehicle page). */
  to?: string;
  /** Shown when `reg` is not a plate (blank, GUV, TBC…). Defaults to the value itself, or "–". */
  fallback?: string;
  /** Stop a click bubbling to a clickable table row (the plate link handles it). */
  stopPropagation?: boolean;
  className?: string;
}

/**
 * A UK number plate drawn at the legal geometry (520 × 111 mm, 79 mm characters, 11 mm
 * spacing, 33 mm group gap) in Aroo's own Charles Wright–style glyphs, scaled to `size`.
 * Announced as "Registration AB12 CDE"; the reg is also invisible text over the glyphs, so it
 * selects and copies like normal text.
 */
export function UkPlate({
  reg,
  size = 'sm',
  band,
  variant = 'rear',
  to,
  fallback,
  stopPropagation = false,
  className,
}: UkPlateProps) {
  const rawId = useId();
  const plate = isPlateValue(reg);
  const showBand = (band ?? (size === 'md' || size === 'lg' ? 'uk' : 'none')) === 'uk';
  const detail = size === 'md' || size === 'lg';
  const text = plate ? formatReg(reg) : '';

  const { markup, width } = useMemo(() => {
    if (!plate) return { markup: '', width: 0 };
    const layout = layoutPlate(text, { band: showBand });
    return {
      markup: renderPlateSvg(text, {
        variant,
        band: showBand,
        flag: size === 'md' || size === 'lg',
        detail,
        idPrefix: `pl${rawId}`,
      }),
      width: layout.width,
    };
  }, [plate, text, showBand, variant, size, detail, rawId]);

  if (!plate) {
    const shown = fallback ?? (reg && reg.trim() ? reg.trim() : '–');
    return <span className={`${classes.plain} ${className ?? ''}`}>{shown}</span>;
  }

  const h = PLATE_SIZES[size];
  const style = {
    '--plate-h': `${h}px`,
    '--plate-w': `${Math.round((h * width) / PLATE_HEIGHT)}px`,
    '--plate-band': showBand ? `${(h * BAND_WIDTH) / PLATE_HEIGHT}px` : '0px',
  } as CSSProperties;

  const body = (
    <span
      className={`${classes.root} ${to ? '' : (className ?? '')}`}
      style={style}
      data-size={size}
      role="img"
      aria-label={`Registration ${text}`}
    >
      <span
        className={classes.art}
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: markup }}
      />
      <span className={classes.copy} aria-hidden="true">
        {text}
      </span>
    </span>
  );

  if (!to) return body;
  return (
    <Link
      to={to}
      className={`${classes.link} ${className ?? ''}`}
      style={style}
      data-size={size}
      onClick={stopPropagation ? (e: MouseEvent) => e.stopPropagation() : undefined}
    >
      {body}
    </Link>
  );
}
